import React from "react";
import { formatImageUrl } from "@/lib/general";

interface DefaultAvatarProps {
  firstName?: string;
  lastName?: string;
  className?: string;
}

interface ImageWithFallbackProps extends DefaultAvatarProps {
  src?: string;
  alt?: string;
}

const generateProfileImage = (firstName: string, lastName: string): string => {
  // Fixed gradient colors
  const gradientColors = ["#93C5FD", "#818CF8"]; // blue-300 to indigo-400

  // Get initials from first and last name
  const initials = `${firstName.charAt(0).toUpperCase()}${lastName
    .charAt(0)
    .toUpperCase()}`;

  // Create SVG with gradient background and initials
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
      <defs>
        <linearGradient id="gradient" gradientTransform="rotate(45 0.5 0.5)">
          <stop offset="0%" style="stop-color:${gradientColors[0]}" />
          <stop offset="100%" style="stop-color:${gradientColors[1]}" />
        </linearGradient>
      </defs>
      <rect width="400" height="400" fill="url(#gradient)" />
      <text 
        x="200" 
        y="200" 
        font-family="Poppins, sans-serif" 
        font-size="140" 
        font-weight="500" 
        fill="#ffffff" 
        text-anchor="middle" 
        dominant-baseline="central"
      >
        ${initials}
      </text>
    </svg>
  `;

  return svg;
};

const DefaultAvatar: React.FC<DefaultAvatarProps> = ({
  firstName = "",
  lastName = "",
  className = "",
}) => {
  const svgData = generateProfileImage(firstName || "U", lastName || "");
  const svgUrl = `data:image/svg+xml;utf8,${encodeURIComponent(svgData)}`;

  return (
    <img
      src={svgUrl}
      alt={`${firstName} ${lastName}`}
      className={`rounded-full object-cover ${className}`}
    />
  );
};

// HOC for image fallback
const withImageFallback = (WrappedComponent: typeof DefaultAvatar) => {
  return ({ src, alt, ...props }: ImageWithFallbackProps) => {
    const [error, setError] = React.useState(false);

    // Extract first and last name from alt text if available
    const firstName = props.firstName || "";
    const lastName = props.lastName || "";

    if (!src || error) {
      return (
        <WrappedComponent
          firstName={firstName}
          lastName={lastName}
          {...props}
        />
      );
    }

    // Ensure src is a string before passing to formatImageUrl
    const imageUrl =
      typeof src === "string" ? formatImageUrl(src, "medium") : "";

    return (
      <img
        src={imageUrl}
        alt={alt || ""}
        className={`rounded-full object-cover ${props.className || ""}`}
        onError={() => setError(true)}
      />
    );
  };
};

export const AvatarImage = withImageFallback(DefaultAvatar);
export default DefaultAvatar;
